import { useEffect, useState } from "react";

/**
 * Central device-capability detection for Parabolum.
 * Computes ONE source of truth used to scale down effects on weak / mobile
 * devices. Smoothness > effects.
 *
 * Tiers gate: gravity particles, SVG lensing, cursor ripple, non-critical
 * parallax and the heavy atmosphere fog.
 */
let cached = null;

export function getCaps() {
  if (cached) return cached;
  if (typeof window === "undefined") {
    return {
      reduced: false,
      isMobile: false,
      isTouch: false,
      lowPower: false,
      particles: true,
      particleScale: 1,
      lensing: true,
      ripple: true,
      parallax: true,
      atmosphere: true,
    };
  }
  const mm = (q) => (window.matchMedia ? window.matchMedia(q).matches : false);
  const reduced = mm("(prefers-reduced-motion: reduce)");
  const coarse = mm("(pointer: coarse)");
  const w = window.innerWidth;
  const isMobile = coarse || w < 768;
  const cores = navigator.hardwareConcurrency || 8;
  const mem = navigator.deviceMemory || 8;
  const lowPower = reduced || isMobile || cores <= 4 || mem <= 4;

  cached = {
    reduced,
    isMobile,
    isTouch: coarse,
    lowPower,
    // feature gates
    particles: !lowPower,
    particleScale: isMobile ? 0.18 : lowPower ? 0.45 : 1,
    lensing: !lowPower && !isMobile,
    ripple: !lowPower && !coarse,
    parallax: !lowPower,
    atmosphere: !isMobile && !reduced,
  };
  return cached;
}

export function usePerf() {
  const [caps, setCaps] = useState(getCaps);
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        cached = null;
        setCaps(getCaps());
      }, 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return caps;
}
